import React, { useState } from "react";
import useWindowSize from "../../hooks/useWindowSize";
import PeriodToggle from "../../components/dashboard/PeriodToggle";
import {
  ChartWrapper,
  Card,
  CardHeader,
  CardTitle,
  ChartContainer,
  ChartGridContainer,
  YAxisLabels,
  YAxisLabel,
  YAxisKLabel,
  ChartArea,
  GridLines,
  GridLine,
  ChartBars,
  ChartBarContainer,
  ChartBar,
  ChartLabels,
  ChartLabel,
  ChartTooltip,
  TooltipGrowth,
  ChartTooltipTextTop,
  ChartTooltipDate,
  TooltipIcon,
  NoDataMessage,
  HorizontalChartBar,
} from "../../styles/statisticsChart";
import ChartTooltipIcon from "../../assets/svgs/Dashboard/ChartTooltip.svg";

interface ChartDataItem {
  month: string;
  value: number;
  growth: string;
  year: number;
  height: number;
}

interface StatisticsChartProps {
  callback: (period: any) => void;
  data: ChartDataItem[] | null;
}

const StatisticsChart = (props: StatisticsChartProps) => {
  const { width } = useWindowSize();
  const isHorizontal = width < 678;
  const [selectedPeriod, setSelectedPeriod] = useState("Monthly");
  const [activeBar, setActiveBar] = useState<number | null>(null);

  const rawData = props.data && props.data.length > 0 ? props.data : [];

  const maxValue =
    rawData.length > 0
      ? Math.max(...rawData.map((item: ChartDataItem) => item.value))
      : 6000;

  const chartData: ChartDataItem[] = rawData.map((item: ChartDataItem) => ({
    ...item,
    height: maxValue > 0 ? (item.value / maxValue) * 100 : 0,
  }));

  const yAxisMax = Math.ceil(maxValue / 1000) * 1000;
  const yAxisValues = Array.from(
    { length: Math.floor(yAxisMax / 1000) + 1 },
    (_, i) => i
  );

  if (chartData.length === 0) {
    return (
      <ChartWrapper>
        <Card>
          <CardHeader>
            <CardTitle>Statistic</CardTitle>
          </CardHeader>
          <NoDataMessage>No data available</NoDataMessage>
        </Card>
      </ChartWrapper>
    );
  }

  const selectPeriod = (period: any) => {
    setSelectedPeriod(period);
    props.callback(period);
  };

  return (
    <ChartWrapper>
      <Card>
        <CardHeader>
          <CardTitle>Statistic</CardTitle>
          <PeriodToggle
            selectedPeriod={selectedPeriod}
            onPeriodChange={selectPeriod}
          />
        </CardHeader>

        <ChartContainer>
          {isHorizontal && (
            <ChartLabels>
              {chartData.map((data: ChartDataItem, index: number) => (
                <ChartLabel key={index}>{data.month}</ChartLabel>
              ))}
            </ChartLabels>
          )}
          <ChartGridContainer>
            {!isHorizontal && (
              <YAxisLabels>
                <YAxisKLabel>(K)</YAxisKLabel>
                {yAxisValues.map((val) => (
                  <YAxisLabel key={val}>{val}</YAxisLabel>
                ))}
              </YAxisLabels>
            )}

            <ChartArea>
              <GridLines>
                {yAxisValues.map((val) => (
                  <GridLine key={val} />
                ))}
              </GridLines>

              <ChartBars>
                {chartData.map((data, index) => (
                  <ChartBarContainer
                    key={index}
                    onMouseEnter={() => setActiveBar(index)}
                    onMouseLeave={() => setActiveBar(null)}
                  >
                    {isHorizontal ? (
                      <HorizontalChartBar
                        width={data.height}
                        $active={activeBar === index}
                      />
                    ) : (
                      <ChartBar
                        height={data.height}
                        $active={activeBar === index}
                      />
                    )}
                    {activeBar === index && (
                      <ChartTooltip>
                        <TooltipIcon className="mr-8">
                          <img src={ChartTooltipIcon} alt="Growth" />
                        </TooltipIcon>
                        <div>
                          <ChartTooltipDate>
                            {data.month.toUpperCase()} {data.year}
                          </ChartTooltipDate>
                          <ChartTooltipTextTop>
                            S$ {data.value.toFixed(2)}
                          </ChartTooltipTextTop>
                          <TooltipGrowth>{data.growth}</TooltipGrowth>
                        </div>
                      </ChartTooltip>
                    )}
                  </ChartBarContainer>
                ))}
              </ChartBars>
            </ChartArea>
            {isHorizontal && (
              <YAxisLabels>
                {yAxisValues.map((val) => (
                  <YAxisLabel key={val}>{val}K</YAxisLabel>
                ))}
              </YAxisLabels>
            )}
          </ChartGridContainer>

          {!isHorizontal && (
            <ChartLabels>
              {chartData.map((data: ChartDataItem, index: number) => (
                <ChartLabel key={index}>{data.month}</ChartLabel>
              ))}
            </ChartLabels>
          )}
        </ChartContainer>
      </Card>
    </ChartWrapper>
  );
};

export default StatisticsChart;